import { useContext, useMemo } from 'react';
import useCommonStyles from '../../../../../../commonStyles';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import InputConfig from '../../../../../../Components/InputConfig';
import DropDownSingle from '../../../../../../Components/DropDownSingle';
import AbSplitTestConcludeExpModal from './AbSplitTestConcludeExpModal';
import {
	EXPB_DOC_CONSTANTS,
	AB_SPLIT_TEST_CONSTANTS,
	CONFIG_KEYDATA_CONSTANTS
} from '../../../../../../core/constants';

// MUI Imports
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

const { SITE_STATUS } = EXPB_DOC_CONSTANTS;

const {
	CUSTOM_TARGETING: { KEY },
	AB_SPLIT_TYPE: { CONTROL }
} = AB_SPLIT_TEST_CONSTANTS;

const { CUSTOM_TARGETING_KEYDATA } = CONFIG_KEYDATA_CONSTANTS;

// Experiment Status Array for DropDown Single [{label, value, disable}]
const expStatusArray = Object.keys(SITE_STATUS).map((status) => {
	const disableBool = status === SITE_STATUS.CONCLUDED ? true : false; // Disable the listItem if the status is concluded
	return { value: status, label: status, disable: disableBool };
});

/**
 * @name AbSplitTestExperimentCard
 * @description This Component is the UI component for A/B Split Test Experiment card
 * used in ExperimentContextSettings
 */
const AbSplitTestExperimentCard = ({ experimentData, index }) => {
	const { expbDoc, updateExpbDoc, units, userHasExpbDocEditAccess } = useContext(UnitsContext);
	const { updateModalContent } = useContext(Modalv2Context);

	/**
	 * @name updatePropertyOfExpObj
	 * @description This function is used to update a property of experiment object in expb doc
	 */
	const updatePropertyOfExpObj = (key, newVal) => {
		const clonedExpbDoc = structuredClone(expbDoc);
		const allExperimentsArray = clonedExpbDoc.experiments;
		allExperimentsArray[index][key] = newVal;
		updateExpbDoc(clonedExpbDoc);
	};

	// Control and Experiment Section Units for extracting the name and KVP values in experiment card
	const [controlSectionUnit, experimentSectionUnit] = useMemo(() => {
		const controlSectionRef = Object.keys(units).find(
			(sectionRef) =>
				units[sectionRef].videoSectionId === experimentData.controlVideoSectionId &&
				units[sectionRef].abSplitTestData &&
				units[sectionRef].abSplitTestData.splitType === CONTROL
		); // Finding the controlSectionRef by iterating through the units and comparing videoSectionId and abSplitTestData.splitType
		// If controlSectionRef is not found then we return null, because corresponding split test was concluded,
		// hence control and Exp Section Names and KVPs will not be rendered.
		if (!controlSectionRef) {
			return [null, null];
		}

		// extracting experimentSectionRef from counterpartSectionRef property of abSplitTestData in control Unit
		const experimentSectionRef =
			units[controlSectionRef].abSplitTestData &&
			units[controlSectionRef].abSplitTestData.counterpartSectionRef;

		return [units[controlSectionRef], units[experimentSectionRef]];
	}, [units, experimentData.controlVideoSectionId]);

	return (
		<Grid
			container
			spacing={1}
			style={{
				width: '100%',
				margin: '1rem auto'
			}}
		>
			{experimentData && (
				<>
					<Grid item xs={12} style={{ marginBottom: '2rem' }}>
						<Chip label="A/B Split Test" color="primary" />
					</Grid>

					{experimentData.status !== SITE_STATUS.CONCLUDED && (
						<>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Control Section Name"
									isObjectEditor={false}
									directValue={controlSectionUnit?.name}
								/>
							</Grid>
							<Grid item xs={4}>
								<InputConfig
									editable={false}
									label="Experiment Section Name"
									isObjectEditor={false}
									directValue={experimentSectionUnit?.name}
								/>
							</Grid>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Control KVP"
									isObjectEditor={false}
									directValue={
										controlSectionUnit &&
										controlSectionUnit[`${CUSTOM_TARGETING_KEYDATA}.targeting`][KEY]
									}
								/>
							</Grid>
							<Grid item xs={3}>
								<InputConfig
									editable={false}
									label="Experiment KVP"
									isObjectEditor={false}
									directValue={
										experimentSectionUnit &&
										experimentSectionUnit[`${CUSTOM_TARGETING_KEYDATA}.targeting`][KEY]
									}
								/>
							</Grid>
						</>
					)}
					<Grid item xs={3}>
						<InputConfig
							editable={userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED}
							label="Context Url (JIRA)"
							isObjectEditor={false}
							directValue={experimentData.contextUrl}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('contextUrl', newVal)}
						/>
					</Grid>
					<Grid item xs={3}>
						<DropDownSingle
							disabled={
								!userHasExpbDocEditAccess || experimentData.status === SITE_STATUS.CONCLUDED
							}
							isObjectEditor={false}
							directValue={experimentData.status}
							list={expStatusArray}
							label="Status"
							updateDirectValue={(newVal) => updatePropertyOfExpObj('status', newVal)}
							labelStyles={{ width: '100%' }}
							formControlVariant={'outlined'}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Exp Owner Email"
							isObjectEditor={false}
							directValue={experimentData.email}
							editable={false}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('email', newVal)}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							editable={userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED}
							label="Comment"
							isObjectEditor={false}
							directValue={experimentData.comment}
							updateDirectValue={(newVal) => updatePropertyOfExpObj('comment', newVal)}
							multiline
							rows={4}
						/>
					</Grid>
					{userHasExpbDocEditAccess && experimentData.status !== SITE_STATUS.CONCLUDED && (
						<Grid item xs={12}>
							<Button
								color="primary"
								variant="contained"
								onClick={() => {
									updateModalContent(
										<AbSplitTestConcludeExpModal experimentData={experimentData} index={index} />
									);
								}}
								style={{ float: 'right', marginRight: '2rem', marginTop: '2rem' }}
							>
								Conclude Experiment
							</Button>
						</Grid>
					)}
				</>
			)}
		</Grid>
	);
};

export default AbSplitTestExperimentCard;
